import type { ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';

export type DrawerSize = 'sm' | 'md' | 'lg';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** Panel width: sm=400, md=480 (default), lg=640. */
  size?: DrawerSize;
  /** Set false to require an explicit close action. Default true. */
  dismissable?: boolean;
  footer?: ReactNode;
  children: ReactNode;
}

const SIZE: Record<DrawerSize, string> = {
  sm: 'max-w-[400px]',
  md: 'max-w-[480px]',
  lg: 'max-w-[640px]',
};

/** Right-side slide-over panel. Shares Modal's scrim, focus trap, Escape and
 *  scroll-lock behavior (via Radix Dialog); differs only in placement. */
export default function Drawer({
  open,
  onClose,
  title,
  description,
  size = 'md',
  dismissable = true,
  footer,
  children,
}: DrawerProps) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-(--z-modal) bg-ink/40 animate-scrim" />
        <Dialog.Content
          className={cn(
            'fixed inset-y-0 right-0 z-(--z-modal) flex h-full w-full flex-col',
            'rounded-l-overlay border-l border-border bg-surface-card shadow-modal animate-drawer',
            SIZE[size],
          )}
          onEscapeKeyDown={dismissable ? undefined : (e) => e.preventDefault()}
          onPointerDownOutside={dismissable ? undefined : (e) => e.preventDefault()}
          onInteractOutside={dismissable ? undefined : (e) => e.preventDefault()}
          {...(description ? {} : { 'aria-describedby': undefined })}
        >
          {/* Header is pinned — never inside the scroll container. */}
          <div className="flex shrink-0 items-start justify-between gap-4 border-b border-border-subtle px-5 py-4">
            <div className="min-w-0">
              <Dialog.Title className="t-card text-green-900">{title}</Dialog.Title>
              {description && (
                <Dialog.Description className="t-caption mt-1">{description}</Dialog.Description>
              )}
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                className="-m-1 shrink-0 rounded-control p-1 text-ink-faint transition-colors hover:text-ink"
              >
                <X size={20} />
              </button>
            </Dialog.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

          {footer && (
            <div className="flex shrink-0 justify-end gap-2 border-t border-border-subtle px-5 py-4">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
